<template>
  <div class="produtos-container">

    <!-- ================= HEADER ================= -->
    <div class="page-header">
      <div class="d-flex flex-column">
        <div class="d-flex align-center flex-wrap">
          <div class="d-flex align-center">
            <v-avatar size="48" color="blue-darken-1" class="mr-3" variant="tonal">
              <v-icon color="white" size="28">mdi-package-variant-closed</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-bold text-grey-darken-4 mb-1">Estoque por Depósito</h1>
              <div class="text-caption text-grey">
                Produtos com estoque detalhado por depósito
              </div>
            </div>
          </div>
          
          <v-spacer />
          
          <div class="d-flex align-center mt-2 mt-sm-0">
            <v-btn
              color="grey-darken-1"
              variant="text"
              @click="voltarMenu"
              class="text-none mr-2"
              prepend-icon="mdi-arrow-left"
            >
              Voltar
            </v-btn>
            
           
          </div>
        </div>
      </div>
    </div>

    <!-- ================= FILTROS EXPANSÍVEIS ================= -->
    <v-card class="filters-card mb-4" elevation="2">
      <v-card-title class="filters-header pa-4">
        <div class="d-flex align-center w-100">
          <!-- Seção Esquerda - Título e Info -->
          <div class="d-flex align-center cursor-pointer flex-grow-1" @click="toggleFilters">
            <v-icon class="mr-2" color="white">mdi-filter-variant</v-icon>
            <span class="filters-title">Filtros</span>
            
            <div class="d-flex align-center gap-2 ml-4">
              <v-chip variant="tonal" size="small">
                {{ pagination.total }} registros
              </v-chip>
              
              <v-chip 
                v-if="hasActiveFilters"
                color="warning" 
                variant="tonal" 
                size="small"
              >
                {{ activeFiltersCount }} filtro(s)
              </v-chip>
            </div>
          </div>
          
          <!-- Seção Central - Botões de Ação -->
          <div class="d-flex align-center gap-2 mr-2">
            <v-btn
              color="white"
              variant="flat"
              size="small"
              @click="fetchData"
              :loading="loading"
            >
              <v-icon size="18" color="grey-darken-2">mdi-refresh</v-icon>
              <span class="ml-2 text-grey-darken-2">Atualizar</span>
            </v-btn>
            
            <v-btn
              class="ml-5"
              color="white"
              variant="flat"
              size="small"
              @click="limparFiltros"
              :disabled="!hasActiveFilters"
            >
              <v-icon size="18" :color="hasActiveFilters ? 'grey-darken-2' : 'grey-lighten-1'">mdi-filter-off</v-icon>
              <span class="ml-2" :class="hasActiveFilters ? 'text-grey-darken-2' : 'text-grey-lighten-1'">Limpar</span>
            </v-btn>
          </div>
          
          <!-- Botão Expandir/Recolher -->
          <v-btn
            icon
            variant="text"
            size="small"
            color="white"
            @click="toggleFilters"
          >
            <v-icon size="20" :class="{ 'rotate-180': filtersExpanded }">
              mdi-chevron-down
            </v-icon>
            <v-tooltip activator="parent">
              {{ filtersExpanded ? 'Recolher Filtros' : 'Expandir Filtros' }}
            </v-tooltip>
          </v-btn>
        </div>
      </v-card-title>
      
      <!-- Conteúdo Expansível -->
      <v-expand-transition>
        <div v-show="filtersExpanded">
          <v-divider />
          <v-card-text class="pa-4">
            <v-row dense>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filtros.codigo_produto"
                  label="Código do Produto"
                  placeholder="Ex: 001..."
                  prepend-inner-icon="mdi-barcode"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  @keyup.enter="fetchData"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filtros.nome_produto"
                  label="Nome do Produto"
                  placeholder="Buscar por nome..."
                  prepend-inner-icon="mdi-magnify"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  @keyup.enter="fetchData"
                />
              </v-col>

              <v-col cols="12" md="2">
                <v-select
                  v-model="filtros.codigo_grupo_produto"
                  :items="gruposProdutos"
                  item-title="nome_grupo_produto"
                  item-value="codigo_grupo"
                  label="Grupo"
                  placeholder="Todos"
                  prepend-inner-icon="mdi-shape-outline"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                />
              </v-col>

              <v-col cols="12" md="2">
                <v-select
                  v-model="filtros.codigo_deposito"
                  :items="depositos"
                  item-title="nome_deposito"
                  item-value="id_deposito"
                  label="Depósito"
                  placeholder="Todos"
                  prepend-inner-icon="mdi-warehouse"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                />
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  v-model.number="filtros.estoque_fisico_min"
                  label="Est. Físico ≥"
                  placeholder="Mín."
                  prepend-inner-icon="mdi-package-variant"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  type="number"
                  @keyup.enter="fetchData"
                />
              </v-col>
            </v-row>

            <v-row dense class="mt-2">
              <v-col cols="12" md="2">
                <v-text-field
                  v-model.number="filtros.comprometido_min"
                  label="Comprom. ≥"
                  placeholder="Mín."
                  prepend-inner-icon="mdi-lock"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  type="number"
                  @keyup.enter="fetchData"
                />
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  v-model.number="filtros.disponivel_min"
                  label="Disponível ≥"
                  placeholder="Mín."
                  prepend-inner-icon="mdi-check-circle"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  type="number"
                  @keyup.enter="fetchData"
                />
              </v-col>

              <v-col cols="12" md="8" class="d-flex justify-end align-center">
                <v-btn
                  color="blue-darken-1"
                  variant="flat"
                  @click="fetchData"
                  :loading="loading"
                >
                  <v-icon start>mdi-filter</v-icon>
                  Filtrar
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- ================= GRID / TABELA ================= -->
    <v-card class="table-card" elevation="2">
      <v-data-table-server
        v-model:items-per-page="itemsPerPage"
        :headers="headers"
        :items="produtos"
        :items-length="pagination.total"
        :loading="loading"
        :page="pagination.current_page"
        :sort-by="sortBy"
        item-key="codigo_produto"
        class="scrollable-table"
        density="compact"
        fixed-header
        height="calc(100vh - 400px)"
        @update:options="onOptionsUpdate"
      >
        <!-- Código -->
        <template #item.codigo_produto="{ item }">
          <v-chip
            color="blue-darken-1"
            variant="tonal"
            size="small"
          >
            {{ item.codigo_produto }}
          </v-chip>
        </template>

        <!-- Nome -->
        <template #item.nome_produto="{ item }">
          <v-chip
            color="teal"
            variant="tonal"
            size="small"
          >
            {{ item.nome_produto }}
          </v-chip>
        </template>

        <!-- Grupo -->
        <template #item.nome_grupo_produto="{ item }">
          <v-chip
            color="purple"
            variant="tonal"
            size="x-small"
          >
            {{ item.nome_grupo_produto }}
          </v-chip>
        </template>

        <!-- Depósito -->
        <template #item.nome_deposito="{ item }">
          <v-chip
            color="blue"
            variant="tonal"
            size="x-small"
          >
            {{ item.nome_deposito }}
          </v-chip>
        </template>

        <template #item.estoque_fisico="{ item }">
          <v-btn
            variant="text"
            size="small"
            :class="saldoCalculadoItem(item, 'deposito') > 0 ? 'text-success font-weight-bold' : 'text-grey'"
            @click="abrirDialogLocalizacao(item)"
            class="pa-0"
            :disabled="saldoCalculadoItem(item, 'deposito') <= 0"
          >
            {{ formatNumber(item.estoque_fisico) }}
            <v-icon v-if="saldoCalculadoItem(item, 'deposito') > 0" size="14" class="ml-1">mdi-magnify</v-icon>
          </v-btn>
        </template>

        <!-- Estoque Comprometido -->
        <template #item.estoque_comprometido="{ item }">
          <span :class="item.estoque_comprometido > 0 ? 'text-warning' : 'text-grey'">
            {{ formatNumber(item.estoque_comprometido) }}
          </span>
        </template>

        <!-- Estoque Disponível -->
        <template #item.estoque_disponivel="{ item }">
          <span :class="item.estoque_disponivel > 0 ? 'text-success font-weight-bold' : 'text-error'">
            {{ formatNumber(item.estoque_disponivel) }}
          </span>
        </template>

        <template #item.saldo_gestor_pendente="{ item }">
          <span :class="saldoGestorItem(item) >= 0 ? 'text-success font-weight-bold' : 'text-error font-weight-bold'">
            {{ formatNumberSigned(saldoGestorItem(item)) }}
          </span>
        </template>

        <template #item.saldo_projetado="{ item }">
          <span class="text-primary font-weight-black">
            {{ formatNumber(saldoCalculadoItem(item, 'deposito')) }}
          </span>
        </template>

        <!-- Nenhum resultado -->
        <template #no-data>
          <div class="text-center pa-6 text-grey">
            Nenhum produto encontrado. Ajuste os filtros e clique em "Filtrar".
          </div>
        </template>
      </v-data-table-server>
    </v-card>

    <!-- ================= DIALOG ESTOQUE POR LOCALIZAÇÃO (BIN) ================= -->
    <v-dialog v-model="dialogLocalizacao" max-width="1100" scrollable>
      <v-card>
        <v-card-title class="bg-teal-darken-1 text-white d-flex align-center">
          <v-icon class="mr-2">mdi-map-marker-outline</v-icon>
          Saldos por Localização
          <v-spacer />
          <span class="text-caption">ref# dg-cpe-loc</span>
        </v-card-title>
        <v-card-subtitle class="bg-teal-darken-1 text-white pb-3">
          <strong>{{ itemSelecionado?.nome_deposito }}</strong> - {{ itemSelecionado?.nome_produto }}
        </v-card-subtitle>
        
        <v-card-text class="pa-0">
          <v-data-table
            :headers="headersEstoqueLocalizacao"
            :items="estoquesLocalizacao"
            :loading="loadingLocalizacao"
            density="compact"
            class="elevation-0 localizacao-table"
            :items-per-page="-1"
            fixed-header
            height="420"
            :hide-default-footer="true"
            :items-per-page-options="[]"
          >
            <!-- Customização de Rua/Prateleira/Nivel/Posição -->
            <template #item.codigo_localizacao="{ item }">
              <span class="font-weight-medium">
                {{ item.codigo_localizacao || 'SEM LOCALIZACAO' }}
              </span>
            </template>
            <template #item.rua="{ item }">
              <v-chip color="secondary" variant="tonal" size="x-small" label>
                {{ item.rua || '-' }}
              </v-chip>
            </template>
            <template #item.prateleira="{ item }">
              <v-chip color="info" variant="tonal" size="x-small" label>
                {{ item.prateleira || '-' }}
              </v-chip>
            </template>
            <template #item.nivel="{ item }">
              <v-chip color="primary" variant="tonal" size="x-small" label>
                {{ item.nivel || '-' }}
              </v-chip>
            </template>
            <template #item.posicao="{ item }">
              <v-chip color="brown" variant="tonal" size="x-small" label>
                {{ item.posicao || '-' }}
              </v-chip>
            </template>

            <template #item.estoque_localizacao="{ item }">
              <span class="text-success font-weight-bold">
                {{ formatNumber(item.estoque_localizacao) }}
              </span>
            </template>

            <template #item.saldo_gestor_pendente="{ item }">
              <span :class="saldoGestorItem(item) >= 0 ? 'text-success font-weight-bold' : 'text-error font-weight-bold'">
                {{ formatNumberSigned(saldoGestorItem(item)) }}
              </span>
            </template>

            <template #item.saldo_projetado="{ item }">
              <span class="text-primary font-weight-black">
                {{ formatNumber(saldoCalculadoItem(item, 'localizacao')) }}
              </span>
            </template>

            <template #body.append>
              <tr class="localizacao-total-row">
                <td class="font-weight-black">TOTAL GERAL</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td class="text-right text-success font-weight-black">
                  {{ formatNumber(totaisLocalizacao.estoque) }}
                </td>
                <td class="text-right font-weight-black" :class="totaisLocalizacao.gestor >= 0 ? 'text-success' : 'text-error'">
                  {{ formatNumberSigned(totaisLocalizacao.gestor) }}
                </td>
                <td class="text-right text-primary font-weight-black">
                  {{ formatNumber(totaisLocalizacao.calculado) }}
                </td>
              </tr>
            </template>
            
            <template #no-data>
              <div class="text-center pa-4 text-grey">
                Nenhuma localização encontrada para este produto neste depósito.
              </div>
            </template>
          </v-data-table>
        </v-card-text>
        
        <v-divider />
        
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn color="grey" variant="text" @click="dialogLocalizacao = false">
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { sapeJodarcService } from "@/services/sapeJodarcService";
import { useToast } from "vue-toastification";

const toast = useToast();

export default {
  name: "ConsultaProdutosEstoques",

  data() {
    return {
      produtos: [],
      gruposProdutos: [],
      depositos: [],
      loading: false,
      initialized: false,
      itemsPerPage: 50,
      filtersExpanded: false,
      sortBy: [],

      // Dialog estoque por localização
      dialogLocalizacao: false,
      loadingLocalizacao: false,
      itemSelecionado: null,
      estoquesLocalizacao: [],

      filtros: {
        codigo_produto: "",
        nome_produto: "",
        codigo_grupo_produto: null,
        codigo_deposito: null,
        estoque_fisico_min: null,
        comprometido_min: null,
        disponivel_min: null,
      },

      pagination: {
        total: 0,
        per_page: 50,
        current_page: 1,
        last_page: 1,
        from: 0,
        to: 0,
      },

      headers: [
        { title: "Código", key: "codigo_produto", width: "120px" },
        { title: "Produto", key: "nome_produto" },
        { title: "Grupo", key: "nome_grupo_produto", width: "150px" },
        { title: "Depósito", key: "nome_deposito", width: "150px" },
        { title: "Est. Físico", key: "estoque_fisico", width: "100px", align: "end" },
        { title: "Comprometido", key: "estoque_comprometido", width: "110px", align: "end" },
        { title: "Disponível", key: "estoque_disponivel", width: "100px", align: "end" },
        { title: "Gestor", key: "saldo_gestor_pendente", width: "100px", align: "end" },
        { title: "Calculado", key: "saldo_projetado", width: "110px", align: "end" },
      ],

      headersEstoqueLocalizacao: [
        { title: "Localização", key: "codigo_localizacao" },
        { title: "Rua", key: "rua" },
        { title: "Prat.", key: "prateleira" },
        { title: "Nível", key: "nivel" },
        { title: "Pos.", key: "posicao" },
        { title: "Estoque", key: "estoque_localizacao", align: "end" },
        { title: "Gestor", key: "saldo_gestor_pendente", align: "end" },
        { title: "Calculado", key: "saldo_projetado", align: "end" },
      ],
    };
  },

  computed: {
    hasActiveFilters() {
      return (
        this.filtros.codigo_produto ||
        this.filtros.nome_produto ||
        this.filtros.codigo_grupo_produto ||
        this.filtros.codigo_deposito ||
        this.filtros.estoque_fisico_min !== null ||
        this.filtros.comprometido_min !== null ||
        this.filtros.disponivel_min !== null
      );
    },
    activeFiltersCount() {
      let count = 0;
      if (this.filtros.codigo_produto) count++;
      if (this.filtros.nome_produto) count++;
      if (this.filtros.codigo_grupo_produto) count++;
      if (this.filtros.codigo_deposito) count++;
      if (this.filtros.estoque_fisico_min !== null) count++;
      if (this.filtros.comprometido_min !== null) count++;
      if (this.filtros.disponivel_min !== null) count++;
      return count;
    },
    totaisLocalizacao() {
      return (this.estoquesLocalizacao || []).reduce((acc, item) => {
        acc.estoque += Number(item?.estoque_localizacao || 0);
        acc.gestor += this.saldoGestorItem(item);
        acc.calculado += this.saldoCalculadoItem(item, 'localizacao');
        return acc;
      }, {
        estoque: 0,
        gestor: 0,
        calculado: 0,
      });
    },
  },

  methods: {
    toggleFilters() {
      this.filtersExpanded = !this.filtersExpanded;
    },

    async fetchData() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
          ...this.filtros,
          ...this.montarFiltrosProdutoFlexiveis(),
          sort_by: this.sortBy.length > 0 ? this.sortBy[0].key : undefined,
          sort_order: this.sortBy.length > 0 ? this.sortBy[0].order : undefined,
        };

        const response = await sapeJodarcService.sapeProdutosEstoquesDepositos(params);

        if (response?.success) {
          this.produtos = Array.isArray(response.data) ? response.data : [];
          if (response.pagination) {
            this.pagination = response.pagination;
            this.inputPage = response.pagination.current_page;
          }
        } else {
          this.produtos = [];
          toast.error(response?.message || "Erro ao carregar produtos");
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao carregar produtos");
        this.produtos = [];
      } finally {
        this.loading = false;
      }
    },

    montarFiltrosProdutoFlexiveis() {
      const codigo = String(this.filtros.codigo_produto || '').trim();
      const nome = String(this.filtros.nome_produto || '').trim();
      const filtros = {};

      if (nome) {
        filtros.nome_produto = nome;
      }

      if (codigo) {
        const pareceDescricao = /[%_\s]/.test(codigo) || !/\d/.test(codigo);
        if (pareceDescricao && !nome) {
          filtros.nome_produto = codigo;
          filtros.codigo_produto = undefined;
        } else {
          filtros.codigo_produto = codigo;
        }
      }

      return filtros;
    },

    async fetchGrupos() {
      try {
        const response = await sapeJodarcService.sapeProdutosGrupo({});
        if (response?.success) {
          this.gruposProdutos = Array.isArray(response.data) ? response.data : [];
        }
      } catch (error) {
        console.error("Erro ao carregar grupos:", error);
      }
    },

    async fetchDepositos() {
      try {
        const response = await sapeJodarcService.sapeDeposito({});
        if (response?.success) {
          this.depositos = Array.isArray(response.data) ? response.data : [];
        }
      } catch (error) {
        console.error("Erro ao carregar depósitos:", error);
      }
    },

    onOptionsUpdate({ page, itemsPerPage, sortBy }) {
      if (this.loading) return;
      
      const currentSortBy = JSON.stringify(this.sortBy);
      const newSortBy = JSON.stringify(sortBy || []);
      
      if (!this.initialized) {
        this.initialized = true;
        this.pagination.current_page = page;
        this.pagination.per_page = itemsPerPage;
        this.itemsPerPage = itemsPerPage;
        this.sortBy = sortBy || [];
        this.fetchData();
        return;
      }
      
      const pageChanged = page !== this.pagination.current_page;
      const perPageChanged = itemsPerPage !== this.pagination.per_page;
      const sortChanged = currentSortBy !== newSortBy;
      
      if (!pageChanged && !perPageChanged && !sortChanged) return;
      
      this.pagination.current_page = page;
      this.itemsPerPage = itemsPerPage;
      this.pagination.per_page = itemsPerPage;
      this.sortBy = sortBy || [];
      this.fetchData();
    },

    limparFiltros() {
      this.filtros = {
        codigo_produto: "",
        nome_produto: "",
        codigo_grupo_produto: null,
        codigo_deposito: null,
        estoque_fisico_min: null,
        comprometido_min: null,
        disponivel_min: null,
      };
      this.pagination.current_page = 1;
      this.fetchData();
    },

    formatNumber(value) {
      if (value === null || value === undefined) return "0";
      return Number(value).toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    formatNumberSigned(value) {
      const number = Number(value || 0);
      const formatted = Math.abs(number).toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
      return number > 0 ? `+${formatted}` : number < 0 ? `-${formatted}` : "0";
    },

    isSameDeposito(id1, id2) {
      if (id1 === null || id1 === undefined || id2 === null || id2 === undefined) return false;
      const s1 = String(id1).trim();
      const s2 = String(id2).trim();
      return s1 === s2 || s1.padStart(2, '0') === s2.padStart(2, '0') || Number(s1) === Number(s2);
    },

    saldoGestorItem(item) {
      return Number(item?.entradas_pendentes || 0) - Number(item?.saidas_pendentes || 0);
    },

    saldoCalculadoItem(item, nivel = 'deposito') {
      if (item?.saldo_projetado !== undefined && item?.saldo_projetado !== null) {
        return Number(item.saldo_projetado || 0);
      }

      const base = nivel === 'localizacao'
        ? Number(item?.estoque_localizacao || 0)
        : Number(item?.estoque_fisico || 0);

      return base + this.saldoGestorItem(item);
    },

    textoLocalizacao(valor) {
      const texto = String(valor || '').trim().replace(/\s+/g, ' ');
      if (!texto || texto === '-' || texto.toUpperCase() === 'SEM LOCALIZACAO') {
        return 'SEM LOCALIZACAO';
      }
      return texto;
    },

    textoEndereco(valor) {
      const texto = String(valor || '').trim().replace(/\s+/g, ' ');
      if (!texto || texto === '-' || texto.toUpperCase() === 'SEM LOCALIZACAO') {
        return '';
      }
      return texto;
    },

    isSemLocalizacao(item) {
      const codigo = this.textoLocalizacao(item?.codigo_localizacao || item?.localizacao || item?.posicao);
      const rua = this.textoEndereco(item?.rua);
      const prateleira = this.textoEndereco(item?.prateleira);
      const nivel = this.textoEndereco(item?.nivel);
      const posicao = this.textoLocalizacao(item?.posicao);
      return codigo === 'SEM LOCALIZACAO' && !rua && !prateleira && !nivel && posicao === 'SEM LOCALIZACAO';
    },

    adicionarSemLocalizacao(mapa, base) {
      const key = 'SEM_LOCALIZACAO';
      if (!mapa[key]) {
        mapa[key] = {
          codigo_localizacao: 'SEM LOCALIZACAO',
          localizacao: 'SEM LOCALIZACAO',
          rua: '',
          prateleira: '',
          nivel: '',
          posicao: 'SEM LOCALIZACAO',
          estoque_localizacao: 0,
          entradas_pendentes: 0,
          saidas_pendentes: 0,
          saldo_projetado: 0,
        };
      }

      mapa[key].estoque_localizacao += Number(base.estoque_localizacao || 0);
      mapa[key].entradas_pendentes += Number(base.entradas_pendentes || 0);
      mapa[key].saidas_pendentes += Number(base.saidas_pendentes || 0);
      mapa[key].saldo_projetado += Number(base.saldo_projetado || 0);
    },

    agruparEstoquesLocalizacao(itens, deposito) {
      const mapa = {};

      (itens || []).forEach(item => {
        const semLocalizacao = this.isSemLocalizacao(item);
        const codigo = semLocalizacao
          ? 'SEM LOCALIZACAO'
          : this.textoLocalizacao(item.codigo_localizacao || item.localizacao || item.posicao);
        const rua = semLocalizacao ? '' : this.textoEndereco(item.rua);
        const prateleira = semLocalizacao ? '' : this.textoEndereco(item.prateleira);
        const nivel = semLocalizacao ? '' : this.textoEndereco(item.nivel);
        const posicao = semLocalizacao ? 'SEM LOCALIZACAO' : (item.posicao || codigo);
        const key = semLocalizacao
          ? 'SEM_LOCALIZACAO'
          : [codigo, rua, prateleira, nivel, posicao].map(v => String(v || '').trim()).join('|');

        if (!mapa[key]) {
          mapa[key] = {
            ...item,
            codigo_localizacao: codigo,
            localizacao: codigo,
            rua,
            prateleira,
            nivel,
            posicao,
            estoque_localizacao: 0,
            entradas_pendentes: 0,
            saidas_pendentes: 0,
            saldo_projetado: 0,
          };
        }

        mapa[key].estoque_localizacao += Number(item.estoque_localizacao || 0);
        mapa[key].entradas_pendentes += Number(item.entradas_pendentes || 0);
        mapa[key].saidas_pendentes += Number(item.saidas_pendentes || 0);
        mapa[key].saldo_projetado += Number(item.saldo_projetado ?? this.saldoCalculadoItem(item, 'localizacao'));
      });

      const totalDeposito = this.saldoCalculadoItem(deposito, 'deposito');
      const totalLocalizado = Object.values(mapa).reduce((acc, item) => acc + this.saldoCalculadoItem(item, 'localizacao'), 0);
      const diferencaSemLocalizacao = Math.round((totalDeposito - totalLocalizado) * 10000) / 10000;

      if (Object.keys(mapa).length === 0) {
        this.adicionarSemLocalizacao(mapa, {
          estoque_localizacao: Number(deposito?.estoque_fisico || 0),
          entradas_pendentes: Math.max(this.saldoGestorItem(deposito), 0),
          saidas_pendentes: Math.max(-this.saldoGestorItem(deposito), 0),
          saldo_projetado: totalDeposito,
        });
      } else if (Math.abs(diferencaSemLocalizacao) > 0.0001) {
        this.adicionarSemLocalizacao(mapa, {
          estoque_localizacao: 0,
          entradas_pendentes: Math.max(diferencaSemLocalizacao, 0),
          saidas_pendentes: Math.max(-diferencaSemLocalizacao, 0),
          saldo_projetado: diferencaSemLocalizacao,
        });
      }

      return Object.values(mapa).sort((a, b) => {
        if (a.codigo_localizacao === 'SEM LOCALIZACAO') return 1;
        if (b.codigo_localizacao === 'SEM LOCALIZACAO') return -1;
        return String(a.codigo_localizacao || '').localeCompare(String(b.codigo_localizacao || ''));
      });
    },

    voltarMenu() {
      this.$router.push({ name: "Menu" });
    },

    async abrirDialogLocalizacao(item) {
      if (this.saldoCalculadoItem(item, 'deposito') <= 0) return;
      
      this.itemSelecionado = item;
      this.dialogLocalizacao = true;
      this.loadingLocalizacao = true;
      this.estoquesLocalizacao = [];

      try {
        const params = {
          codigo_produto: item.codigo_produto,
          codigo_deposito: item.codigo_deposito,
          per_page: 1000,
        };
        const response = await sapeJodarcService.sapeProdutosEstoquesLocalizacoes(params);

        if (response?.success) {
          const localizacoes = Array.isArray(response.data?.data)
            ? response.data.data
            : (Array.isArray(response.data) ? response.data : []);
          const localizacoesDoDeposito = localizacoes.filter(loc => {
            const codigoDeposito = loc.codigo_deposito || loc.deposito || loc.id_deposito;
            return !codigoDeposito || this.isSameDeposito(codigoDeposito, item.codigo_deposito);
          });
          this.estoquesLocalizacao = this.agruparEstoquesLocalizacao(localizacoesDoDeposito, item);
        } else {
          toast.error(response?.message || "Erro ao carregar localizações");
        }
      } catch (error) {
        console.error("Erro ao carregar localizações:", error);
        toast.error("Erro ao carregar estoques por localização");
      } finally {
        this.loadingLocalizacao = false;
      }
    },
  },

  mounted() {
    this.fetchGrupos();
    this.fetchDepositos();
  },
};
</script>

<style scoped>
/* ✅ CONTAINER PRINCIPAL */
.produtos-container {
  padding: 24px;
  background: #e4edfb;
  min-height: 100vh;
}

.localizacao-table :deep(.v-table__wrapper) {
  overflow-y: auto !important;
}

.localizacao-table :deep(.v-data-table-footer) {
  display: none !important;
}

.localizacao-total-row {
  position: sticky;
  bottom: 0;
  z-index: 2;
  background: #f8fafc;
  box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.12);
}

.page-header {
  margin-bottom: 16px;
}

/* ✅ ESTILOS DOS FILTROS */
.filters-card {
  border-radius: 12px;
  overflow: hidden;
}

.filters-header {
  background: linear-gradient(135deg, #1976d2, #1565c0);
  color: white;
}

.filters-title {
  font-size: 1rem;
  font-weight: 600;
}

/* ✅ ROTAÇÃO DO ÍCONE EXPANDIR */
.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}

.cursor-pointer {
  cursor: pointer;
}

/* ✅ ESTILOS DA TABELA */
.table-card {
  border-radius: 12px;
  overflow: hidden;
}

/* ✅ SCROLL CUSTOMIZADO PARA TABELA */
.scrollable-table :deep(.v-table__wrapper) {
  overflow-x: auto !important;
  overflow-y: auto !important;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #1976d2, #2196f3);
  border-radius: 4px;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #1565c0, #1976d2);
}

/* ✅ HEADERS DA TABELA */
.scrollable-table :deep(.v-data-table__th) {
  font-weight: 600 !important;
  background-color: #f8f9fa !important;
  position: sticky !important;
  top: 0 !important;
  z-index: 10 !important;
}

/* ✅ COLUNA CÓDIGO FIXA */
.scrollable-table :deep(.v-data-table__th:first-child),
.scrollable-table :deep(.v-data-table__td:first-child) {
  position: sticky !important;
  left: 0 !important;
  z-index: 5 !important;
  background: white !important;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1) !important;
}

.scrollable-table :deep(.v-data-table__th:first-child) {
  z-index: 15 !important;
  background: #f8f9fa !important;
}

/* ✅ HOVER DA LINHA */
.scrollable-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(25, 118, 210, 0.05) !important;
}

.scrollable-table :deep(.v-data-table__tr:hover .v-data-table__td:first-child) {
  background-color: rgba(25, 118, 210, 0.05) !important;
}

/* ✅ TRUNCATE */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ✅ RESPONSIVIDADE */
@media (max-width: 768px) {
  .produtos-container {
    padding: 16px;
  }
  
  .scrollable-table :deep(.v-data-table__th),
  .scrollable-table :deep(.v-data-table__td) {
    font-size: 0.875rem;
  }
}
</style>
